import { useState } from "react";
import { Link } from "react-router-dom";
import type { Review } from "@/types/movie";

interface ReviewSectionProps {
  reviews: Review[];
  movieTitle: string;
}

function Stars({ rating }: { rating: number }) {
  const rounded = Math.round(rating / 2);
  return (
    <span className="flex items-center gap-0.5 text-primary-400">
      {Array.from({ length: 5 }).map((_, index) => (
        <i key={index} className={`${index < rounded ? "ri-star-fill" : "ri-star-line"} text-sm`} />
      ))}
    </span>
  );
}

export default function ReviewSection({ reviews, movieTitle }: ReviewSectionProps) {
  const [liked, setLiked] = useState<string[]>([]);

  const toggleLike = (id: string) =>
    setLiked((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));

  return (
    <section className="mt-12">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
          <i className="ri-chat-quote-line text-primary-400" />
          影评与观众点评
        </h2>
        <span className="text-xs text-foreground-500">共 {reviews.length} 条</span>
      </div>

      {reviews.length === 0 ? (
        <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300/60 px-6 py-14 text-center">
          <i className="ri-quill-pen-line text-4xl text-foreground-500" />
          <p className="mt-4 text-sm text-foreground-300">这部影片还没有收录影评</p>
          <p className="mt-1 text-xs text-foreground-500">去影评专题看看编辑的深度解读吧</p>
          <Link
            to="/special"
            className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300/60 px-5 py-2.5 text-sm text-foreground-100 transition-colors hover:border-primary-400 hover:text-primary-400"
          >
            <i className="ri-article-line text-base" />
            进入影评专题
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {reviews.map((review) => {
            const isLiked = liked.includes(review.id);
            return (
              <article
                key={review.id}
                className="rounded-lg border border-background-200/60 bg-background-100 p-5"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 font-heading text-sm font-bold text-accent-400">
                      {review.author.slice(0, 1)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-100">{review.author}</p>
                      <div className="mt-0.5 flex items-center gap-2">
                        <Stars rating={review.rating} />
                        <span className="text-xs text-foreground-500">{review.rating.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-foreground-500">{review.date}</span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground-300">{review.content}</p>
                <div className="mt-4 flex items-center justify-between border-t border-background-200/50 pt-3">
                  <span className="text-xs text-foreground-500">对《{movieTitle}》的评价</span>
                  <button
                    type="button"
                    onClick={() => toggleLike(review.id)}
                    className={`inline-flex items-center gap-1.5 text-xs transition-colors ${
                      isLiked ? "text-primary-400" : "text-foreground-400 hover:text-primary-400"
                    }`}
                  >
                    <i className={`${isLiked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} text-sm`} />
                    {review.likes + (isLiked ? 1 : 0)}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}